import { DirectoryUser, PfRole, Userdata } from "@/types/user";
import { USERS_ADMIN_TAGS } from "@/api/users-admin.constraints";
import { baseApi } from "@/api/baseApi";
import { SUPPLIER_TAGS } from "@/api/suppliers.constraints";

const supplierOwnerOptionsTag = { type: SUPPLIER_TAGS.SUPPLIER_OWNER_OPTIONS, id: "LIST" } as const;

interface SharedUserPayload {
  password: string;
  name: string;
  surname: string;
  phone_number: string | undefined;
  avatar: string;
  is_active: boolean;
  pf_roles?: PfRole[];
}

export interface CreateUserPayload extends SharedUserPayload {
  username: string;
  email: string;
}

export interface UpdateUserPayload extends SharedUserPayload {
  id: string;
  email?: never;
}

export interface AdminEmailChangeRequestPayload {
  userId: string;
  newEmail: string;
}

export type AdminEmailChangeRequestResponse = { status: "pending"; channel: null };

const optionalTransportFields = <T extends SharedUserPayload>(payload: T) => ({
  ...payload,
  password: payload.password || undefined,
  avatar: payload.avatar || undefined,
});

export const toCreateUserData = (payload: CreateUserPayload) => optionalTransportFields(payload);

export const toUpdateUserData = ({ id: _id, email: _email, ...payload }: UpdateUserPayload) =>
  optionalTransportFields(payload);

export const toAdminEmailChangeRequestData = (payload: Pick<AdminEmailChangeRequestPayload, "newEmail">) => ({
  new_email: payload.newEmail,
});

export const activeDirectoryUsersRequest = () => ({
  url: "/pf-auth/users/directory",
  params: { active_only: true },
});

export const usersAdminApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    createUser: builder.mutation<void, CreateUserPayload>({
      invalidatesTags: [USERS_ADMIN_TAGS.USERS_ADMIN_TAG],
      query: user => ({ url: "/pf-auth/users", method: "POST", data: toCreateUserData(user) }),
    }),
    updateUser: builder.mutation<void, UpdateUserPayload>({
      invalidatesTags: [USERS_ADMIN_TAGS.USERS_ADMIN_TAG, supplierOwnerOptionsTag],
      query: user => ({
        url: `/pf-auth/users/${user.id}`,
        method: "PATCH",
        data: toUpdateUserData(user),
      }),
    }),
    requestAdminEmailChange: builder.mutation<AdminEmailChangeRequestResponse, AdminEmailChangeRequestPayload>({
      query: ({ userId, ...payload }) => ({
        url: `/pf-auth/users/${userId}/email-change/requests`,
        method: "POST",
        data: toAdminEmailChangeRequestData(payload),
      }),
    }),
    getUsers: builder.query<Userdata[], string[] | undefined | void>({
      providesTags: [USERS_ADMIN_TAGS.USERS_ADMIN_TAG],
      query: users => ({
        url: "/pf-auth/users",
        params: {
          users: users ? users.join(",") : undefined,
        },
      }),
    }),
    getDirectoryUser: builder.query<DirectoryUser, string>({
      providesTags: [USERS_ADMIN_TAGS.USERS_DIRECTORY_DETAILS_TAG],
      transformResponse: (response: DirectoryUser[]) => response[0],
      query: user => ({
        url: "/pf-auth/users/directory",
        params: {
          users: user,
        },
      }),
    }),
    getDirectoryUsers: builder.query<DirectoryUser[], string[] | undefined | void>({
      providesTags: [USERS_ADMIN_TAGS.USERS_DIRECTORY_TAG],
      query: users => ({
        url: "/pf-auth/users/directory",
        params: {
          users: users ? users.join(",") : undefined,
        },
      }),
    }),
    getActiveDirectoryUsers: builder.query<DirectoryUser[], void>({
      providesTags: [USERS_ADMIN_TAGS.USERS_DIRECTORY_TAG],
      query: activeDirectoryUsersRequest,
    }),
  }),
});

export const {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useRequestAdminEmailChangeMutation,
  useGetDirectoryUsersQuery,
  useGetDirectoryUserQuery,
  useGetActiveDirectoryUsersQuery,
} = usersAdminApi;
